<!-- Header -->
<div class="generator-header">
  <div class="header-left">
    <div class="header-icon">
      <dojo-icon icon="chart-scatter" [size]="14" faPrefix="fas" />
    </div>
    <h2 class="header-title">Data Generator</h2>
  </div>
  <dojo-icon-button icon="xmark" size="xs" [transparent]="true" (click)="closePanel.emit()" />
</div>

<!-- Accordion -->
<mat-accordion displayMode="flat" class="generator-accordion">
  <!-- Library Panel -->
  <mat-expansion-panel
    class="generator-panel"
    [expanded]="libraryExpanded()"
    (opened)="libraryExpanded.set(true)"
    (closed)="libraryExpanded.set(false)"
  >
    <mat-expansion-panel-header>
      <mat-panel-title>
        <dojo-icon icon="book-open" [size]="13" />
        Library
      </mat-panel-title>
    </mat-expansion-panel-header>

    <ng-template matExpansionPanelContent>
      <div class="library-list">
        @for (dataset of libraryDatasets; track dataset.id) {
          <button class="library-item" (click)="onLoadDataset(dataset)">
            <div class="library-item-icon">
              <dojo-icon [icon]="dataset.icon" [size]="13" />
            </div>
            <div class="library-item-info">
              <span class="library-item-name">{{ dataset.name }}</span>
              <span class="library-item-meta">{{ dataset.description }}</span>
            </div>
            <span class="library-item-badge">n={{ dataset.xValues.length }}</span>
          </button>
        }
      </div>
    </ng-template>
  </mat-expansion-panel>

  <!-- Custom (Correlate) Panel -->
  <mat-expansion-panel
    class="generator-panel"
    [expanded]="customExpanded()"
    (opened)="customExpanded.set(true)"
    (closed)="customExpanded.set(false)"
  >
    <mat-expansion-panel-header>
      <mat-panel-title>
        <dojo-icon icon="chart-line" [size]="13" />
        Correlate
      </mat-panel-title>
    </mat-expansion-panel-header>

    <ng-template matExpansionPanelContent>
      <!-- Preview -->
      <section class="section">
        <div class="preview-card">
          <div class="preview-label">PREVIEW</div>
          <div class="preview-chart-wrapper">
            <div echarts [options]="previewChartOptions()" [initOpts]="initOpts" class="preview-chart"></div>
          </div>
          <div class="preview-r-display">
            {{ previewMetricLabel() }}&nbsp;=&nbsp;<span class="preview-r-value">{{ previewMetricValue() }}</span>
          </div>
        </div>
      </section>

      <!-- Relationship Type -->
      <section class="section">
        <div class="section-label">RELATIONSHIP TYPE</div>
        <div class="relationship-grid">
          @for (card of relationshipCards; track card.type) {
            <button
              class="relationship-card"
              [class.selected]="relationshipType() === card.type"
              (click)="selectRelationshipType(card.type)"
            >
              <span class="relationship-label">{{ card.label }}</span>
              <span class="relationship-subtitle" [innerHTML]="card.safeSubtitleHtml"></span>
            </button>
          }
        </div>
      </section>

      <!-- Direction Toggle (for non-linear types) -->
      @if (showDirection()) {
        <section class="section">
          <div class="section-label">DIRECTION</div>
          <mat-button-toggle-group
            class="custom-toggle-group size-xs direction-toggle"
            hideSingleSelectionIndicator
            [value]="curveDirection()"
            (change)="setCurveDirection($event.value)"
          >
            @for (opt of directionOptions(); track opt.value) {
              <mat-button-toggle [value]="opt.value">
                <dojo-icon [icon]="opt.icon" [size]="12" class="mr-1" />
                {{ opt.label }}
              </mat-button-toggle>
            }
          </mat-button-toggle-group>
        </section>
      }

      <!-- Target Strength (r or R²) -->
      <section class="section">
        <div class="section-label">{{ strengthLabel() }}</div>
        @if (isRSquaredMode()) {
          <input
            type="range"
            class="correlation-slider"
            min="0"
            max="1"
            step="0.01"
            [value]="targetStrength()"
            [style.--slider-progress]="strengthSliderProgress()"
            (input)="onStrengthSlider($event)"
          />
          <div class="correlation-labels">
            <span>0.00</span>
            <span>0.50</span>
            <span>1.00</span>
          </div>
        } @else {
          <input
            type="range"
            class="correlation-slider"
            min="-1"
            max="1"
            step="0.01"
            [value]="targetStrength()"
            [style.--slider-progress]="strengthSliderProgress()"
            (input)="onStrengthSlider($event)"
          />
          <div class="correlation-labels">
            <span>-1.00</span>
            <span>0.00</span>
            <span>+1.00</span>
          </div>
        }
        <div class="preset-row">
          @for (preset of strengthPresets(); track preset) {
            <dojo-button
              [size]="'s'"
              [fill]="targetStrength() === preset ? 'outline' : 'clear'"
              [customColor]="targetStrength() === preset ? 'sky' : undefined"
              (click)="setTargetStrength(preset)"
            >
              @if (!isRSquaredMode() && preset >= 0) {
                +
              }
              {{ preset.toFixed(1) }}
            </dojo-button>
          }
        </div>
      </section>

      <!-- Sample Size -->
      <section class="section">
        <div class="section-label">
          SAMPLE SIZE
          <span class="sample-size-value">{{ sampleSize() }}</span>
        </div>
        <input
          type="range"
          class="sample-slider"
          min="5"
          max="200"
          step="1"
          [value]="sampleSize()"
          [style.--slider-progress]="sampleSliderProgress()"
          (input)="onSampleSizeSlider($event)"
        />
        <div class="preset-row">
          @for (n of sampleSizePresets; track n) {
            <dojo-button
              [size]="'s'"
              [fill]="sampleSize() === n ? 'outline' : 'clear'"
              [customColor]="sampleSize() === n ? 'sky' : undefined"
              (click)="setSampleSize(n)"
            >
              n={{ n }}
            </dojo-button>
          }
        </div>
      </section>

      <!-- X / Y Labels -->
      <section class="section">
        <div class="label-row">
          <div class="label-field">
            <div class="section-label">X label</div>
            <dojo-text-input [formCtrl]="xLabelCtrl" placeholder="x" [compact]="true" />
          </div>
          <div class="label-field">
            <div class="section-label">Y label</div>
            <dojo-text-input [formCtrl]="yLabelCtrl" placeholder="y" [compact]="true" />
          </div>
        </div>
      </section>

      <!-- Decimal Places -->
      <section class="section">
        <div class="section-label mb-1">DECIMAL PLACES</div>
        <div class="preset-row">
          @for (dp of decimalPlacesOptions; track dp) {
            <dojo-button
              [size]="'s'"
              [fill]="decimalPlaces() === dp ? 'outline' : 'clear'"
              [customColor]="decimalPlaces() === dp ? 'sky' : undefined"
              (click)="setDecimalPlaces(dp)"
            >
              {{ dp }}
            </dojo-button>
          }
        </div>
      </section>

      <!-- Generate Button -->
      <div class="generate-action">
        <dojo-button [size]="'l'" [fill]="'solid'" customColor="sky" (click)="onGenerate()">
          <dojo-icon icon="wand-magic-sparkles" [size]="14" faPrefix="fas" />
          {{ generateLabel() }}
        </dojo-button>
      </div>
    </ng-template>
  </mat-expansion-panel>
</mat-accordion>
